// Storage - thin wrapper over localStorage with safe fallback
// Used for best score and user settings (mute).

const KEY_BEST_SCORE = 'korovany.bestScore';
const KEY_BEST_WAVE = 'korovany.bestWave';
const KEY_MUTED = 'korovany.muted';
const KEY_AUTO_ATTACK = 'korovany.autoAttack';
const KEY_PLAYER_NAME = 'korovany.playerName';
const KEY_RELICS_UNLOCKED = 'korovany.relicsUnlocked';
const KEY_RELICS_ACTIVE = 'korovany.relicsActive';
// First-session onboarding signal. `sessionsStarted` increments at the
// start of every run (used by game.js to decide whether to apply the
// novice starting kit, mercy heal, and draft bias). Only first-ever
// runs in this browser (counter === 0 at startGame) get onboarded.
// The W4 mercy heal is gated in-memory by Game._mercyHealUsed; no
// separate storage key needed.
const KEY_SESSIONS_STARTED = 'korovany.sessionsStarted';
// Language picked with the menu's EN/RU button; unset = browser language.
const KEY_LANG = 'korovany.lang';
export const PLAYER_NAME_MAX = 20;

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // storage unavailable (private mode, quota) — silently ignore
  }
}

export function getBestScore() {
  const v = safeGet(KEY_BEST_SCORE);
  const n = v === null ? 0 : parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function getBestWave() {
  const v = safeGet(KEY_BEST_WAVE);
  const n = v === null ? 0 : parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Record a finished run. Updates best score / best wave if surpassed.
// Returns true if either record was broken.
export function recordRun(score, wave) {
  let broken = false;
  if (score > getBestScore()) {
    safeSet(KEY_BEST_SCORE, String(score));
    broken = true;
  }
  if (wave > getBestWave()) {
    safeSet(KEY_BEST_WAVE, String(wave));
    broken = true;
  }
  return broken;
}

export function getMuted() {
  return safeGet(KEY_MUTED) === '1';
}

export function setMuted(muted) {
  safeSet(KEY_MUTED, muted ? '1' : '0');
}

export function getSavedLang() {
  return safeGet(KEY_LANG);
}

export function setSavedLang(code) {
  safeSet(KEY_LANG, code);
}

// Auto-attack toggle (Caps Lock / touch lock gesture). Off by default —
// it's an accessibility/comfort option, not the intended default input.
export function getAutoAttack() {
  return safeGet(KEY_AUTO_ATTACK) === '1';
}

export function setAutoAttack(on) {
  safeSet(KEY_AUTO_ATTACK, on ? '1' : '0');
}

export function getPlayerName() {
  const v = safeGet(KEY_PLAYER_NAME);
  return v && v.trim() ? v.trim() : '';
}

// Trim + length-cap so a runaway paste can't break the leaderboard layout.
export function setPlayerName(name) {
  if (typeof name !== 'string') return '';
  const cleaned = name.trim().slice(0, PLAYER_NAME_MAX);
  if (cleaned) safeSet(KEY_PLAYER_NAME, cleaned);
  return cleaned;
}

// --- Relics (meta-progression) ---
//
// Two sets persist per browser: unlocked (everything the player has ever
// collected) and active (the subset the player currently has toggled on
// for the next run). Active is silently filtered through unlocked so a
// stale entry in localStorage can't activate a relic the player doesn't
// actually own.

function readStringSet(key) {
  const raw = safeGet(key);
  if (!raw) return new Set();
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter(s => typeof s === 'string'));
  } catch (e) {
    return new Set();
  }
}

function writeStringSet(key, set) {
  safeSet(key, JSON.stringify([...set]));
}

export function getUnlockedRelics() {
  return readStringSet(KEY_RELICS_UNLOCKED);
}

// Marks a relic id as unlocked. New unlocks default to active so the
// player immediately benefits without an extra menu trip. Returns true
// if this call newly added the id, false if it was already unlocked.
export function unlockRelic(id) {
  if (typeof id !== 'string' || !id) return false;
  const unlocked = getUnlockedRelics();
  if (unlocked.has(id)) return false;
  unlocked.add(id);
  writeStringSet(KEY_RELICS_UNLOCKED, unlocked);
  const active = readStringSet(KEY_RELICS_ACTIVE);
  active.add(id);
  writeStringSet(KEY_RELICS_ACTIVE, active);
  return true;
}

export function getActiveRelics() {
  const unlocked = getUnlockedRelics();
  const active = readStringSet(KEY_RELICS_ACTIVE);
  return new Set([...active].filter(id => unlocked.has(id)));
}

export function setRelicActive(id, on) {
  if (typeof id !== 'string' || !id) return;
  const active = readStringSet(KEY_RELICS_ACTIVE);
  if (on) active.add(id); else active.delete(id);
  writeStringSet(KEY_RELICS_ACTIVE, active);
}

// --- Onboarding (novice-funnel) ---
//
// `getSessionsStarted()` returns the count BEFORE this run started.
// 0 = this is the player's first ever run. Call `incrementSessionsStarted()`
// once at the beginning of each new run so the count tracks attempts
// (not completions) — we want the novice kit to fire on the very first
// session even if the player never finishes a wave.
export function getSessionsStarted() {
  const v = safeGet(KEY_SESSIONS_STARTED);
  const n = v === null ? 0 : parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function incrementSessionsStarted() {
  const next = getSessionsStarted() + 1;
  safeSet(KEY_SESSIONS_STARTED, String(next));
  return next;
}

// True if the next/current run is the player's first ever (no run has
// started yet). Game.js calls this BEFORE incrementSessionsStarted().
export function isFirstSession() {
  return getSessionsStarted() === 0;
}
